import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { getIcon } from "utils/iconsConfig";
import { useGlobalContext } from "context/GlobalContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { postServiceComment, updateServiceRequestStatus } from "features/Setting/Service Request/API/getAllServiceRequest";
import { toast } from "react-toastify";
import { findSpecificMenu, findSpecificSubMenu, isAllowed } from "utils/checkUserRigths";
import { usePermissions } from "context/PermissionsContext";
import RemoveModal from "components/ui/RemoveModal/RemoveModal";

const ActionButtons = ({ status, onAction, setModal }) => {
  const { showProductionImages, approvalTime, canReOpen } = useGlobalContext();
  const permission = usePermissions();
  const { serviceRequestId } = useParams();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const queryClient = useQueryClient();

  const hasAccessManagementAction = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Service Requests"), "management");

  const { mutate: updateStatus } = useMutation({
    mutationFn: updateServiceRequestStatus,
    onSuccess: () => {
      queryClient.invalidateQueries(["service-request-details"]);
      queryClient.invalidateQueries(["service-requests"]);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(message, {
        position: "top-right",
        autoClose: 2000,
      });
    },
  });

  const { mutate: postComment } = useMutation({
    mutationFn: postServiceComment,
    onSuccess: () => {
      queryClient.invalidateQueries(["serviceComment"]);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to add comment";
      toast.error(message, {
        position: "top-right",
        autoClose: 2000,
      });
    },
  });

  const handlePrintSlipSubmit = (values) => {
    const goldWeight = values?.["Gold Gross Weight"] ?? "";
    const diamondWeight = values?.["Diamond Weight"] ?? "";

    const messageParts = [];

    const formatValue = (value, unit) => {
      return !value || Number(value) === 0 ? "N/A" : `${value}${unit}`;
    };

    const formattedGold = formatValue(goldWeight, "g");
    const formattedDiamond = formatValue(diamondWeight, "ct");

    messageParts.push(`Gross Weight: ${formattedGold}`);
    messageParts.push(`Diamond Weight: ${formattedDiamond}`);

    const formData = new FormData();
    formData.append("serviceRequestId", serviceRequestId);
    formData.append("message", messageParts.join(" | "));
    postComment(formData);
  };

  const btnInfo = [
    {
      btnName: "Gold Gross Weight",
      btnPlaceHolder: "Enter Weight",
      showCross: true,
      required: true,
      value: "",
      onlyNumber: true,
      suffix: "g",
    },
    {
      btnName: "Diamond Weight",
      btnPlaceHolder: "Enter Weight",
      showCross: false,
      required: false,
      value: "",
      onlyNumber: true,
      suffix: "ct",
    },
  ];

  if (!hasAccessManagementAction) {
    return null;
  }

  const cancelBtn = (
    <Button
      variant="outlined"
      sx={{
        borderColor: "#E77B7B",
        color: "#E77B7B",
        backgroundColor: "transparent",
        "&:hover": { backgroundColor: "transparent", borderColor: "#E77B7B" },
        textTransform: "none",
        fontFamily: "Poppins",
        fontWeight: 500,
        fontSize: "18px",
        minWidth: 110,
      }}
      onClick={() => {
        setCancelReason("");
        setCancelModalOpen(true);
      }}
    >
      Cancel
    </Button>
  );

  const cancelModal = (
    <RemoveModal
      open={cancelModalOpen}
      setOpen={setCancelModalOpen}
      heading={`Are you sure you want to Cancel #${serviceRequestId}?`}
      cumWidth="500px"
      gap={0}
      btnInfo={[
        {
          btnName: "No",
          bgColor: "#E77B7B",
          click: () => setCancelModalOpen(false),
        },
        {
          btnName: "Yes",
          bgColor: cancelReason.trim() ? "#50C878" : "#8B8B8B",
          disabled: !cancelReason.trim(),
          click: () => {
            const formData = new FormData();
            formData.append("serviceRequestId", serviceRequestId);
            formData.append("message", cancelReason.trim());
            postComment(formData);
            updateStatus({ serviceRequestId, status: "Cancelled" });
            setCancelModalOpen(false);
          },
        },
      ]}
    >
      <Box sx={{ width: "100%", textAlign: "left", marginTop: "26px" }}>
        <div style={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "500", marginBottom: "8px", color: "black" }}>
          Reason<span style={{ color: "red" }}>*</span>
        </div>
        <textarea
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Enter Reason"
          style={{
            width: "100%",
            height: "105px",
            padding: "10px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            fontFamily: "Poppins",
            fontSize: "14px",
            resize: "none",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </Box>
    </RemoveModal>
  );

  let content = null;

  switch (status) {
    case "Review":
      content = (
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#50C878",
              "&:hover": { backgroundColor: "#50C878" },
              minWidth: 110,
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
            }}
            onClick={() =>
              onAction({
                heading: `Are you sure you want to accept #${serviceRequestId}?`,
                btnInfo: [
                  {
                    btnName: "Discard",
                    bgColor: "#E77B7B",
                    click: () => console.log("Cancelled"),
                  },
                  {
                    btnName: "Accept",
                    bgColor: "#50C878",
                    click: () =>
                      updateStatus({
                        serviceRequestId,
                        status: "Accepted",
                      }),
                  },
                ],
              })
            }
          >
            Accept
          </Button>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#E77B7B26",
              "&:hover": { backgroundColor: "#E77B7B26" },
              minWidth: 110,
              color: "#E77B7B",
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
            }}
            onClick={() =>
              onAction({
                heading: `Are you sure you want to reject #${serviceRequestId}?`,
                btnInfo: [
                  {
                    btnName: "Reject",
                    bgColor: "#E77B7B",
                    click: () => console.log("Rejected"),
                  },
                  {
                    btnName: "Keep",
                    bgColor: "#50C878",
                    click: () => console.log("Keep"),
                  },
                ],
              })
            }
          >
            Reject
          </Button>
          {cancelBtn}
        </Stack>
      );
      break;

    case "Accepted":
      content = (
        <Stack direction="row" spacing={2}>
          <div
            style={{
              backgroundColor: "#50C878",
              color: "#000",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
              padding: "12px 10px",
              borderRadius: "5px",
              cursor: "pointer",
              minWidth: 110,
              display: "flex",
              justifyContent: "center",
            }}
            onClick={() =>
              onAction({
                heading: `Did you receive #${serviceRequestId} product?`,
                btnInfo: [
                  {
                    btnName: "No",
                    bgColor: "#E77B7B",
                    click: () => console.log("NO"),
                  },
                  {
                    btnName: "Yes, Receive",
                    bgColor: "#50C878",
                    click: () =>
                      updateStatus({
                        serviceRequestId,
                        status: "Received",
                      }),
                  },
                ],
              })
            }
          >
            Receive
          </div>
          {cancelBtn}
        </Stack>
      );
      break;

    case "Received":
      content = (
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#50C878",
              "&:hover": { backgroundColor: "#50C878" },
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
            }}
            onClick={() =>
              onAction({
                heading: `Are you sure you want to send for production #${serviceRequestId}?`,
                btnInfo: [
                  {
                    btnName: "No",
                    bgColor: "#E77B7B",
                    click: () => console.log("NO"),
                  },
                  {
                    btnName: "Yes, Send",
                    bgColor: "#50C878",
                    click: () => {
                      updateStatus({
                        serviceRequestId,
                        status: "Production",
                      });
                    },
                  },
                ],
              })
            }
          >
            Send For Production
          </Button>
          <Button
            variant="contained"
            disableRipple
            disableElevation
            sx={{
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
                boxShadow: "none",
              },
              "&:active": {
                boxShadow: "none",
              },
              "&:focus": {
                outline: "none",
              },
              width: 98,
              height: 45,
              opacity: 1,
              borderRadius: "5px",
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
              color: "#FFFFFF",
              border: "1px solid #FFFFFF",
              lineHeight: "100%",
              letterSpacing: "0%",
              whiteSpace: "nowrap",
            }}
            onClick={() =>
              setModal({
                title: "Select Printer",
                open: true,
                btnTitle: "Print",
                placeHolder: "Choose Printer",
                btnInfo: btnInfo,
                onSubmit: handlePrintSlipSubmit,
              })
            }
          >
            Print Slip
          </Button>
          {cancelBtn}
        </Stack>
      );
      break;

    case "Production":
      content = (
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            disabled={!showProductionImages}
            sx={{
              backgroundColor: showProductionImages ? "#50C878" : "#8B8B8B",
              "&:hover": { backgroundColor: showProductionImages ? "#50C878" : "#8B8B8B" },
              "&.Mui-disabled": {
                backgroundColor: "#8B8B8B",
                color: "#000",
                cursor: "not-allowed",
                pointerEvents: "auto",
              },
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
              cursor: showProductionImages ? "pointer" : "default",
            }}
            onClick={() =>
              onAction({
                heading: "Are you sure you want to send for client approval?",
                btnInfo: [
                  {
                    btnName: "No",
                    bgColor: "#E77B7B",
                    click: () => console.log("NO"),
                  },
                  {
                    btnName: "Yes, Send",
                    bgColor: "#50C878",
                    click: () =>
                      updateStatus({
                        serviceRequestId,
                        status: "Need your Approval",
                      }),
                  },
                ],
              })
            }
          >
            Send For Client Approval
          </Button>
          {/* <Button
            variant="contained"
            sx={{
              backgroundColor: "#FFD70026",
              "&:hover": { backgroundColor: "#FFD70026" },
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
              color: "#FFD700",
            }}
            onClick={() =>
              onAction({
                heading: "Are you sure you want to bypass client approval?",
                btnInfo: [
                  {
                    btnName: "No",
                    bgColor: "#E77B7B",
                    click: () => console.log("NO"),
                  },
                  {
                    btnName: "Yes, Bypass",
                    bgColor: "#50C878",
                    click: () => updateStatus({
                      serviceRequestId,
                      status: "ByPass Approval",
                    }),
                  },
                ],
              })
            }
          >
            Bypass Client Approval
          </Button> */}
          <Button
            variant="contained"
            // disabled={!showProductionImages}
            sx={{
              backgroundColor: "#FFD70026",
              "&:hover": {
                backgroundColor: "#FFD70026",
              },

              // "&.Mui-disabled": {
              //   backgroundColor: "#8B8B8B",
              //   color: "#000",
              //   cursor: "not-allowed",
              //   pointerEvents: "auto",
              // },

              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
              color: "#FFD700",
              cursor: showProductionImages ? "pointer" : "not-allowed",
              opacity: showProductionImages ? 1 : 1,
            }}
            onClick={() =>
              showProductionImages &&
              onAction({
                heading: "Are you sure you want to bypass client approval?",
                btnInfo: [
                  {
                    btnName: "No",
                    bgColor: "#E77B7B",
                    click: () => console.log("NO"),
                  },
                  {
                    btnName: "Yes, Bypass",
                    bgColor: "#50C878",
                    click: () =>
                      updateStatus({
                        serviceRequestId,
                        status: "ByPass Approval",
                      }),
                  },
                ],
              })
            }
          >
            Bypass Client Approval
          </Button>
          {cancelBtn}
        </Stack>
      );
      break;

    case "Need your Approval":
      content = (
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#40D2FF1A",
              "&:hover": { backgroundColor: "#40D2FF1A" },
              textTransform: "none",
              fontFamily: "Karla",
              color: "#40D2FF",
              padding: "14px 20px",
              borderRadius: "12px",
              cursor: "default",
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              {getIcon("WaitingRoomIcon")}
              <Box textAlign="left">
                <Typography fontSize="18px" fontWeight={600} lineHeight={1.2}>
                  Awaiting for Client Approval
                </Typography>
                <Typography fontSize="16px" fontWeight={600} opacity={0.9}>
                  {`Since – ${approvalTime}`}
                </Typography>
              </Box>
            </Box>
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#FFD70026",
              "&:hover": { backgroundColor: "#FFD70026" },
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
              color: "#FFD700",
            }}
            onClick={() =>
              onAction({
                heading: "Are you sure you want to bypass client approval?",
                btnInfo: [
                  {
                    btnName: "No",
                    bgColor: "#E77B7B",
                    click: () => console.log("NO"),
                  },
                  {
                    btnName: "Yes, Bypass",
                    bgColor: "#50C878",
                    click: () =>
                      updateStatus({
                        serviceRequestId,
                        status: "ByPass Approval",
                      }),
                  },
                ],
              })
            }
          >
            Bypass Client Approval
          </Button>
          {cancelBtn}
        </Stack>
      );
      break;

    case "Ready for Dispatch":
      content = (
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#50C878",
              "&:hover": { backgroundColor: "#50C878" },
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
            }}
            onClick={() =>
              onAction({
                heading: `Are you sure you want to dispatch #${serviceRequestId} now?`,
                btnInfo: [
                  {
                    btnName: "No",
                    bgColor: "#E77B7B",
                    click: () => console.log("NO"),
                  },
                  {
                    btnName: "Yes, Dispatch",
                    bgColor: "#50C878",
                    click: () =>
                      updateStatus({
                        serviceRequestId,
                        status: "Dispatched",
                      }),
                  },
                ],
              })
            }
          >
            Dispatch Now
          </Button>
          {cancelBtn}
        </Stack>
      );
      break;

    case "Dispatched":
      content = (
        <Stack direction="row" spacing={2}>
          <div
            style={{
              backgroundColor: "#8B8B8B",
              "&:hover": { backgroundColor: "#8B8B8B" },
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
              color: "#000",
              cursor: "not-allowed",
              padding: "12px 10px",
              borderRadius: "5px",
            }}
          >
            Dispatched
          </div>
          <Button
            variant="contained"
            // disabled={!canReOpen}
            sx={{
              backgroundColor: !canReOpen ? "rgb(139, 139, 139)" : "#50C878",
              "&:hover": { backgroundColor: !canReOpen ? "#777" : "#50C878" },
              cursor: !canReOpen ? "not-allowed" : "pointer",
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
            }}
            onClick={() => {
              if (!canReOpen) return;
              onAction({
                heading: `Are you sure you want to reopen this #${serviceRequestId}?`,
                btnInfo: [
                  {
                    btnName: "No",
                    bgColor: "#E77B7B",
                    click: () => console.log("NO"),
                  },
                  {
                    btnName: "Yes",
                    bgColor: "#50C878",
                    click: () =>
                      updateStatus({
                        serviceRequestId,
                        status: "Review",
                      }),
                  },
                ],
              });
            }}
          >
            Reopen
          </Button>
          {cancelBtn}
        </Stack>
      );
      break;

    case "Cancelled":
      content = (
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#50C878",
            "&:hover": { backgroundColor: "#50C878" },
            textTransform: "none",
            fontFamily: "Poppins",
            fontWeight: 500,
            fontSize: "18px",
            minWidth: 110,
          }}
          onClick={() =>
            onAction({
              heading: `Are you sure you want to Reopen #${serviceRequestId}?`,
              btnInfo: [
                {
                  btnName: "No",
                  bgColor: "#E77B7B",
                  click: () => console.log("No"),
                },
                {
                  btnName: "Yes",
                  bgColor: "#50C878",
                  click: () => updateStatus({ serviceRequestId, status: "Review" }),
                },
              ],
            })
          }
        >
          Reopen
        </Button>
      );
      break;

    default:
      content = null;
  }

  return (
    <>
      {content}
      {cancelModal}
    </>
  );
};

export default ActionButtons;
